<template>
	<div class="pb-16">
		<transition name="fade">
			<div v-if="showSaveNotification" class="fixed top-0 left-0 right-0 bg-green-500 text-white py-4 px-4 text-center z-50">Changes saved automatically</div>
		</transition>
		<Mango @update:data="setKit" class="flex flex-col w-full items-center" :query="{ depthLimit: 1 }">
			<Spinner v-if="!store.editingKit?.id" />
			<template v-else-if="store.editingKit?.id">
				<div class="w-full max-w-8xl mb-12">
					<div class="p-4" style="font-family: 'Montserrat', sans-serif">
						<div>
							<UploadFile
								@url="store.editingKit.image = $event"
								class="h-[20vh] aspect-video bg-gray-100 cursor-pointer mb-8 flex items-center justify-center relative border-2 rounded-xl shadow-card overflow-hidden"
								:showDelete="false"
								:showProgress="false"
								:cropAspect="16 / 9"
								:cropPrompt="false"
								v-slot="{ progress }"
							>
								<div class="h-4 bg-green-500 absolute left-0 bottom-0" :style="`width: ${progress}%`" />
								<div v-if="!store.editingKit.image" class="w-full h-full flex flex-col items-center justify-center">
									<i class="fa fa-user text-3xl" />
									<div class="mt-2">{{ progress ? 'Uploading...' : 'Upload a Banner Image' }}</div>
								</div>
								<div
									v-else
									class="w-full h-full bg-cover bg-center group relative overflow-hidden max-w-4xl z-20"
									:style="`background-image: url('${store.editingKit.image?.url || store.editingKit.image}')`"
								>
									<div class="w-full h-full hidden group-hover:flex absolute backdrop-blur items-center justify-center">
										<i class="fa fa-pencil text-3xl text-gray-800" />
									</div>
								</div>
								<div
									v-if="store.editingKit.image"
									class="absolute w-full h-full bg-cover bg-center z-10"
									:style="`background-image: url('${store.editingKit.image?.url || store.editingKit.image}')`"
								>
									<div class="absolute w-full h-full backdrop-blur-xl" />
								</div>
							</UploadFile>
						</div>

						<div class="mb-12 flex flex-col items-start">
							<div class="uppercase text-sm tracking-widest font-bold text-black/60 mb-2">Birth Kit Name</div>
							<div class="flex items-center sm:text-3xl font-semibold text-ihh-midnight-gray border rounded-xl overflow-hidden">
								<input
									ref="nameInput"
									v-model="store.editingKit.name"
									@input="updateInputWidth"
									placeholder="Birth Kit Name"
									:style="{ width: inputWidth + 'px' }"
									class="outline-none py-3 pl-4 pr-1"
								/>
								<div class="text-ihh-midnight-gray shrink-0 bg-[#e5e7eb] py-3 pl-1 pr-4 cursor-default">Custom Birth Kit</div>
							</div>
							<div
								v-if="hasOutOfStockItems"
								class="flex mt-6 w-full max-w-52 justify-center items-center text-center text-ihh-white text-xs font-semibold bg-ihh-coral rounded py-2 capitalize"
							>
								Kit contains out of stock items
							</div>
						</div>

						<div class="tracking-wide text-black/60 whitespace-nowrap uppercase text-sm font-bold">Included in your kit</div>
						<Draggable
							v-model="includedProducts"
							group="products"
							item-key="id"
							class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2"
							@add="handleMove($event, false)"
						>
							<template #item="{ element: p }">
								<BirthKitItem
									:key="p.product.id"
									:product="p.product"
									:variant="p.variant"
									:quantity="p.quantity"
									:isBuilder="true"
									@update:quantity="setQuantity(p, $event)"
									@update:variant="p.variant = $event"
								/>
							</template>
						</Draggable>
						<button
							@click="
								() => {
									addItemModalOpen = true
									store.birthKitOptional = false
								}
							"
							class="text-ihh-midnight-gray font-semibold w-1/2 h-24 bg-ihh-dark-salmon rounded-xl mt-4"
						>
							Add Item +
						</button>

						<div class="mt-16">
							<div class="tracking-wide text-black/60 whitespace-nowrap uppercase text-sm font-bold">Optional items</div>
							<Draggable
								v-model="optionalProducts"
								group="products"
								item-key="id"
								class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2"
								@add="handleMove($event, true)"
							>
								<template #item="{ element: p }">
									<BirthKitItem
										:key="p.product.id"
										:product="p.product"
										:variant="p.variant"
										:quantity="p.quantity"
										:isBuilder="true"
										@update:quantity="setQuantity(p, $event)"
										@update:variant="p.variant = $event"
										:optional="true"
									/>
								</template>
							</Draggable>
							<button
								@click="
									() => {
										addItemModalOpen = true
										store.birthKitOptional = true
									}
								"
								class="text-ihh-midnight-gray font-semibold w-1/2 h-24 bg-ihh-dark-salmon rounded-xl mt-4"
							>
								Add Optional Item +
							</button>
						</div>
					</div>
				</div>

				<div v-if="store.editingKit?.id" class="flex w-full justify-center sticky bottom-0 z-10">
					<div
						class="flex w-full justify-center m-auto items-center w-full bg-ihh-white shadow-md px-4 py-10 sm:p-y rounded border border-ihh-gray/50 cursor-pointer"
					>
						<div class="flex w-full max-w-8xl justify-between">
							<div class="flex flex-col w-full justify-between hidden sm:block">
								<div class="text-2xl font-semibold text-ihh-midnight-gray max-w-md tracking-wide mr-6">{{ store.editingKit.name }}</div>
								<button
									v-if="!store.editingKit.isVisible"
									@click="submitForApproval"
									class="bg-ihh-dark-salmon text-ihh-midnight-gray px-6 py-2 rounded hover:bg-ihh-dark-salmon transition-colors mt-2"
								>
									Submit for Approval
								</button>
								<div v-if="hasOutOfStockItems" class="flex w-full justify-start">
									<button class="mt-2 text-xs text-center bg-ihh-coral py-1 px-3 text-ihh-white font-bold capitalize rounded-md">
										Kit contains out of stock items
									</button>
								</div>
							</div>
							<div class="flex w-full justify-between sm:justify-end items-center">
								<div class="flex-col text-ihh-midnight-gray text-center md:text-end mr-6">
									<div class="flex sm:flex-col md:flex-row">
										<div class="text-base xs:text-lg font-semibold mr-2">SUBTOTAL:</div>
										<div class="text-base xs:text-lg font-semibold tracking-widest">
											{{ subtotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
										</div>
									</div>

									<a @click="cartBirthKit" class="flex w-full justify-between whitespace-nowrap">
										<div class="max-w-32 flex w-full">
											<span class="flex w-full justif-end">
												<InfoIcon />
											</span>
											<button @click="shippingModalOpen = true" class="text-ihh-dark-salmon hover:text-ihh-salmon underline text-xs font-semibold">
												Shipping and returns
											</button>
										</div>
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
		</Mango>

		<Modal
			v-if="shippingModalOpen"
			@hide="
				() => {
					shippingModalOpen = false
					store.birthKitOptional = null
				}
			"
		>
			<div class="prose">
				<h2>Shipping and Returns</h2>
				<p>Shipping cost is based on weight. Just add products to your bag and use the Shipping Calculator to view the different options.</p>
				<p>We offer free shipping on orders over $125 shipping to the lower 48 states</p>
				<p>We want you to be 100% satisfied with your purchase. Most items in new condition can be returned or exchanged within 90 days of purchase.</p>
			</div>
		</Modal>

		<Search :show="addItemModalOpen" @hide="addItemModalOpen = false" :navigate="false" />
		<!-- <Modal v-if="addItemModalOpen" @hide="addItemModalOpen = false; optional: null">
			<div class="prose">
				<h2>Add {{ optional ? 'Optional' : 'Primary' }} Products</h2>
			</div>
		</Modal> -->
	</div>
</template>

<script>
import axios from 'axios'
import Product from '../products/product.vue'
import BirthKitItem from '../products/birth-kit-item.vue'
import Swal from 'sweetalert2'
import Modal from '../layout/modal.vue'
import Mango from '../../helpers/mango'
import InfoIcon from '../layout/info-icon.vue'
import UploadFile from '../../helpers/uploadFile.vue'
import Search from '../layout/search.vue'
import Draggable from 'vuedraggable'

export default {
	mounted() {
		this.$nextTick(() => {
			this.updateInputWidth()
		})
	},
	components: { Product, BirthKitItem, Modal, InfoIcon, UploadFile, Search, Product, Draggable },
	inject: ['store'],
	data() {
		return {
			inputWidth: 240,
			showSaveNotification: false,
			shippingModalOpen: false,
			originalKit: null,
			kit: null,
			hasOutOfStockItems: false,
			unsavedChanges: false,
			addItemModalOpen: false,
			saving: false,
			searchTimeout: 1000,
			query: {
				depthLimit: 2,
				fields: ['id', 'name', 'image', 'products', 'products.product', 'products.variant', 'products.quantity', 'products.optional'],
			},
		}
	},
	watch: {
		'store.editingKit': {
			handler(n, o) {
				console.log('o,n', o?.id, n?.id)
				if (o?.id) this.debounceSaveKit()
			},
			deep: true,
		},
		originalKit: {
			handler(kit) {
				if (kit?.id) {
					if (this.store?.user?.roles?.includes('admin')) {
						// Admins can edit any kit
						return
					}
					if (this.store?.user?.id === kit?.authorId && !!this.store?.user?.id) {
						// Authors can only edit their own kits
						return
					}
					// redirect all others to the kit page
					this.store.editingKit = null
					let kitUrl = kit?.customUrl?.url || `${kit?.slug}` || `${kit?.id}`
					this.$router.push(`/birthKits/${kitUrl}`)
				}
			},
			immediate: true,
			deep: true,
		},
	},
	methods: {
		updateInputWidth() {
			const canvas = document.createElement('canvas')
			const context = canvas.getContext('2d')
			const input = this.$refs.nameInput
			if (!input) return
			const computedStyle = window.getComputedStyle(input)

			context.font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`

			const text = input.value || input.placeholder
			const textWidth = context.measureText(text).width

			// Add padding and some buffer space
			const padding = parseInt(computedStyle.paddingLeft) + parseInt(computedStyle.paddingRight)
			const width = Math.max(0, textWidth + padding + 20)

			this.inputWidth = width
		},
		handleMove(evt, isOptional) {
			console.log('handleMove', evt)
			const itemId = evt.item?.attributes?.['product-id']?.value
			const item = this.store.editingKit.products.find((p) => p.product.id == itemId)
			item.optional = isOptional
		},
		setKit(kit) {
			kit = kit?.birthKit || kit
			kit.name = kit.name.replace(/ Custom Birth Kit/gi, '')
			this.originalKit = JSON.parse(JSON.stringify(kit))
			kit.products = kit?.products?.sort((a, b) => a.product.name.localeCompare(b.product.name)) || []
			if (this.store.editingKit?.id == kit.id) return this.saveKit()
			this.store.editingKit = kit

			this.hasOutOfStockItems = this.kitHasOutOfStockProducts(this.originalKit)

			this.$nextTick(() => {
				this.updateInputWidth()
			})
		},
		async saveKit() {
			if (!this.store?.editingKit?.id) return
			console.log('saving!')
			this.saving = true
			let data = {
				id: this.store.editingKit.id,
				name: this.store.editingKit.name.replace(/ Custom Birth Kit/gi, '').trim() + ' Custom Birth Kit',
				products: this.store.editingKit.products,
				image: this.store.editingKit.image?.url || this.store.editingKit.image,
			}
			data = JSON.parse(JSON.stringify(data))
			data.products.forEach((p) => (p.quantity = p.optional ? 0 : p.quantity))
			await Mango.birthKits.save(data)
			this.unsavedChanges = false
			this.saving = false
			this.showSaveNotification = true
			setTimeout(() => (this.showSaveNotification = false), 3000)
		},
		setQuantity(p, q) {
			// console.log('p, q', p, q)
			// let product = this.store.editingKit.products.find(c => c.product.id == p.product.id)
			// console.log('product', product)
			if (q > 0) return (p.quantity = q)
			this.store.editingKit.products = this.store.editingKit.products.filter((c) => c.product.id != p.product.id)
		},
		async submitForApproval() {
			try {
				const response = await axios.post(`${this.store.api}/controllers/birthKit/submitForApproval`, {
					kitId: this.store.editingKit.id,
				})

				if (response.data.error) {
					Swal.fire({ text: response.data.error, icon: 'error' })
					return
				}

				Swal.fire({
					text: 'Your kit has been submitted for approval. Our team will review it and contact you soon.',
					icon: 'success',
				})
			} catch (error) {
				console.error('Error submitting kit for approval:', error)
				Swal.fire({
					text: 'There was an error submitting your kit for approval. Please try again later.',
					icon: 'error',
				})
			}
		},
		variantsOutOfStock(product) {
			return product?.variants?.every((v) => v.inventoryLevel <= 0) && product.inventoryTracking === 'variant'
		},
		productAndVariantsOutOfStock(product) {
			return product?.outOfStock || this.variantsOutOfStock(product) || !product?.isVisible
		},
		kitHasOutOfStockProducts(kit) {
			return kit?.products?.some((p) => this.productAndVariantsOutOfStock(p.product))
		},
		// getOutOfStockProducts(kit) {
		// 	return kit?.products?.filter((p) => this.productAndVariantsOutOfStock(p.product))
		// },
	},
	computed: {
		includedProducts() {
			return this.store.editingKit.products.filter((p) => !p.optional)
		},
		optionalProducts() {
			return this.store.editingKit.products.filter((p) => p.optional)
		},
		debounceSaveKit() {
			return () => {
				this.unsavedChanges = true
				clearTimeout(this.searchTimeout)
				this.searchTimeout = setTimeout(() => {
					this.saveKit()
				}, 1000)
			}
		},
		optionalProducts() {
			return this.store.editingKit?.products?.filter((p) => p.optional && p.product?.id)
		},
		// outOfStock() {
		// 	return this.store.editingKit?.products?.some((p) => {
		// 		p.product?.outOfStock ||
		// 			(p.product?.inventoryTracking === 'variant' && p.variant?.inventoryLevel === 0) ||
		// 			(p.product?.inventoryTracking === 'product' && p.product?.inventoryLevel === 0)
		// 	})
		// },
		// Subtotal calculation (total before tax)
		subtotal() {
			return this.store.editingKit?.products
				?.filter((p) => !p.optional) // Filter out optional products
				?.reduce((total, p) => {
					let price = p.variant?.currentPrice || p.product?.currentPrice || p.variant?.calculatedPrice || p.product?.calculatedPrice
					let rules = p.product?.bulkPricingRules
					// if (rules?.length) console.log('rules', rules)
					for (let rule of rules) {
						// console.log('rule', rule)
						if (p.quantity >= rule.quantityMin && (p.quantity <= rule.quantityMax || !rule.quantityMax)) {
							price = rule.type === 'fixed' ? rule.amount : rule.type === 'price' ? price - rule.amount : price * rule.amount
							// console.log('price', price)
						}
					}
					return total + price * p.quantity
				}, 0)
		},
		// Texas State Sales Tax (8.21% of the subtotal)
		texasStateSalesTax() {
			const taxRate = 0.0821
			return this.subtotal * taxRate
		},
		// Total price including the tax
		totalPrice() {
			const totalWithTax = parseFloat(this.subtotal) + parseFloat(this.texasStateSalesTax)
			return totalWithTax // Return the total with tax formatted as a string
		},
	},
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
